import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { petAPI } from '../../../../services/api';
import RestrictedVisitModal from '../../../reception/RestrictedVisitModal';
import SOAPDetailsContent from '../../../reception/SOAPDetailsContent';


const MedicalHistoryTab = ({ petId }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeInternalTab, setActiveInternalTab] = useState('vaccination');
    const [pastVisits, setPastVisits] = useState([]);
    const [loadingVisits, setLoadingVisits] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!petId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await petAPI.getCompleteMedicalHistory(petId);
            if (response.data.status === 'success') {
                setHistory(response.data.data);
            } else {
                setError(t('receptionOutpatients.medicalHistory.errors.genericMessage', { defaultValue: 'Failed to load medical history' }));
            }
        } catch (err) {
            console.error('Error fetching medical history:', err);
            // Check if it's a 500 error (backend issue)
            if (err.response?.status === 500) {
                setError('Unable to load medical history due to a server error. Please try again later or contact support if the issue persists.');
            } else {
                setError('Failed to load medical history. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [petId, t]);

    const fetchPastVisits = useCallback(async () => {
        if (!petId) return;
        setLoadingVisits(true);
        try {
            const response = await petAPI.getPetVisits(petId);
            if (response.data.status === 'success') {
                const visits = response.data.data || [];
                // Only show visits that are checked out, completed, or closed
                const filtered = visits.filter(v =>
                    !['active', 'admitted', 'registered', 'pending', 'in_progress', 'ongoing', 'cancelled', 'canceled'].includes(String(v.status || '').toLowerCase())
                );
                setPastVisits(filtered);
            } else if (Array.isArray(response.data)) {
                const filtered = response.data.filter(v =>
                    !['active', 'admitted', 'registered', 'pending', 'in_progress', 'ongoing', 'cancelled', 'canceled'].includes(String(v.status || '').toLowerCase())
                );
                setPastVisits(filtered);
            }
        } catch (err) {
            console.error('Error fetching past visits:', err);
        } finally {
            setLoadingVisits(false);
        }
    }, [petId]);

    useEffect(() => {
        if (activeInternalTab === 'past_visits' || activeInternalTab === 'medication') {
            fetchPastVisits();
        }
    }, [activeInternalTab, fetchPastVisits]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const formatDate = (dateString) => {
        if (!dateString) return '---';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const medicationList = useMemo(() => {
        const historyMeds = history?.medication_history || [];
        const serviceMeds = (pastVisits || []).flatMap((visit) => {
            const services = visit.medication_services || [];
            return services.map((service, index) => ({
                id: `service-${visit.id}-${service.id || index}`,
                title: service.title || service.name_en || service.name_ar,
                medication_name: service.title || service.name_en || service.name_ar,
                date: visit.visit_date,
                start_date: visit.visit_date,
                veterinarian: visit.doctor_name,
                note: [
                    `${visit.type === 'inpatient'
                        ? t('receptionInpatients.title', { defaultValue: 'Inpatient' })
                        : t('receptionOutpatients.title', { defaultValue: 'Outpatient' })}${visit.serial_num ? ` · ${visit.serial_num}` : ''}`,
                    service.qty ? `${t('common.qty', { defaultValue: 'Qty' })}: ${service.qty}` : null,
                    service.notes || null,
                    service.due_date ? `${t('common.dueDate', { defaultValue: 'Due' })}: ${service.due_date}` : null,
                ].filter(Boolean).join(' · '),
            }));
        });
        return [...serviceMeds, ...historyMeds];
    }, [history, pastVisits, t]);

    if (loading) return (
        <div className="tab-loading">
            <div className="spinner"></div>
            <p>{t('common.loading')}</p>
        </div>
    );

    if (error) return (
        <div className="tab-error" style={{ 
            padding: '24px', 
            textAlign: 'center',
            color: '#ef4444'
        }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: '12px' }}></i>
            <p style={{ margin: '8px 0', fontWeight: 500 }}>{error}</p>
            <button 
                onClick={fetchHistory}
                style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                }}
            >
                {t('visitReports.retry', { defaultValue: 'Retry' })}
            </button>
        </div>
    );

    if (!history) return <div className="tab-empty">{t('common.noData')}</div>;

    const internalTabs = [
        { key: 'vaccination', label: t('receptionOutpatients.medicalHistory.tabs.vaccination'), icon: 'fas fa-syringe' },
        { key: 'medication', label: t('receptionOutpatients.medicalHistory.tabs.medication'), icon: 'fas fa-pills' },
        { key: 'allergies', label: t('receptionOutpatients.medicalHistory.tabs.allergies'), icon: 'fas fa-allergies' },
        { key: 'illness', label: t('receptionOutpatients.medicalHistory.tabs.illness'), icon: 'fas fa-disease' },
        { key: 'surgery', label: t('receptionOutpatients.medicalHistory.tabs.surgery'), icon: 'fas fa-procedures' },
        { key: 'dental', label: t('receptionOutpatients.medicalHistory.tabs.dental'), icon: 'fas fa-tooth' },
        { key: 'deworming', label: t('receptionOutpatients.medicalHistory.tabs.deworming', { defaultValue: 'Deworming & Anti-Flea' }), icon: 'fas fa-bug' },
        { key: 'past_visits', label: t('receptionInpatients.details.tabs.pastVisits', { defaultValue: 'Past Visits' }), icon: 'fas fa-history' }
    ];

    const getField = (obj, fields) => {
        for (const field of fields) {
            if (obj[field] !== undefined && obj[field] !== null) return obj[field];
        }
        return null;
    };

    const renderContent = () => {
        switch (activeInternalTab) {
            case 'vaccination':
                return (
                    <div className="history-section-content">
                        {history.vaccination_history?.length > 0 ? (
                            history.vaccination_history.map((v, i) => (
                                <div key={i} className="history-card-premium">
                                    <div className="card-header">
                                        <span className="title">{getField(v, ['title', 'vaccine_name', 'Vaccine Name']) || '---'}</span>
                                        <span className="date">{formatDate(getField(v, ['date', 'date_administered', 'Date Administered']))}</span>
                                    </div>
                                    <div className="card-footer">
                                        <span className="vet"><i className="fas fa-user-md"></i> {getField(v, ['veterinarian', 'Veterinarian']) || '---'}</span>
                                        {v.next_due_date && <span className="next-due"><i className="fas fa-calendar-check"></i> Next: {formatDate(getField(v, ['next_due_date', 'Next Due Date']))}</span>}
                                    </div>
                                    {v.note && <p className="notes">{v.note}</p>}
                                </div>
                            ))
                        ) : <div className="empty-section">{t('receptionOutpatients.medicalHistory.sections.vaccination.empty')}</div>}
                    </div>
                );
            case 'medication':
                return (
                    <div className="history-section-content">
                        {medicationList.length > 0 ? (
                            medicationList.map((m, i) => (
                                <div key={i} className="history-card-premium">
                                    <div className="card-header">
                                        <span className="title">{getField(m, ['title', 'medication_name', 'Medication Name']) || '---'}</span>
                                        <span className="date">{formatDate(getField(m, ['date', 'start_date', 'Start Date']))}</span>
                                    </div>
                                    <div className="card-footer">
                                        <span className="vet"><i className="fas fa-user-md"></i> {getField(m, ['veterinarian', 'Veterinarian']) || '---'}</span>
                                    </div>
                                    {m.note && <p className="notes">{m.note}</p>}
                                </div>
                            ))
                        ) : <div className="empty-section">{t('receptionOutpatients.medicalHistory.sections.medication.empty')}</div>}
                    </div>
                );
            case 'allergies':
                return (
                    <div className="history-section-content">
                        {history.current_allergies?.length > 0 ? (
                            history.current_allergies.map((a, i) => (
                                <div key={i} className="history-card-premium border-red">
                                    <div className="card-header">
                                        <span className="title">{getField(a, ['title', 'allergen', 'Allergen']) || (typeof a === 'string' ? a : '---')}</span>
                                        {a.severity && <span className={`severity-badge ${a.severity.toLowerCase()}`}>{a.severity}</span>}
                                    </div>
                                    {a.reaction && <div className="card-body"><strong>Reaction:</strong> {a.reaction}</div>}
                                    <div className="card-footer">
                                        <span className="date">{formatDate(getField(a, ['date', 'date_recorded', 'Date Recorded']))}</span>
                                    </div>
                                    {a.note && <p className="notes">{a.note}</p>}
                                </div>
                            ))
                        ) : <div className="empty-section">{t('receptionOutpatients.medicalHistory.sections.allergies.empty')}</div>}
                    </div>
                );
            case 'illness':
                return (
                    <div className="history-section-content">
                        {history.past_illness_history?.length > 0 || history.chronic_conditions?.length > 0 ? (
                            [...(history.past_illness_history || []), ...(history.chronic_conditions || [])].map((ill, i) => (
                                <div key={i} className="history-card-premium">
                                    <div className="card-header">
                                        <span className="title">{getField(ill, ['title', 'illness_name', 'condition_name', 'Illness Name', 'Condition Name']) || '---'}</span>
                                        <span className="date">{formatDate(getField(ill, ['date', 'diagnosis_date', 'Diagnosis Date']))}</span>
                                    </div>
                                    {ill.treatment_plan && <div className="card-body"><strong>Treatment:</strong> {ill.treatment_plan}</div>}
                                    <div className="card-footer">
                                        <span className="vet"><i className="fas fa-user-md"></i> {getField(ill, ['veterinarian', 'Veterinarian']) || '---'}</span>
                                    </div>
                                    {ill.note && <p className="notes">{ill.note}</p>}
                                </div>
                            ))
                        ) : <div className="empty-section">{t('receptionOutpatients.medicalHistory.sections.illness.empty')}</div>}
                    </div>
                );
            case 'surgery':
                return (
                    <div className="history-section-content">
                        {history.surgical_history?.length > 0 ? (
                            history.surgical_history.map((s, i) => (
                                <div key={i} className="history-card-premium">
                                    <div className="card-header">
                                        <span className="title">{getField(s, ['title', 'surgery_name', 'Surgery Name']) || '---'}</span>
                                        <span className="date">{formatDate(s.date || s.Date)}</span>
                                    </div>
                                    <div className="card-footer">
                                        <span className="vet"><i className="fas fa-user-md"></i> {getField(s, ['veterinarian', 'Veterinarian']) || '---'}</span>
                                    </div>
                                    {s.note && <p className="notes">{s.note}</p>}
                                </div>
                            ))
                        ) : <div className="empty-section">{t('receptionOutpatients.medicalHistory.sections.surgery.empty')}</div>}
                    </div>
                );
            case 'dental':
                return (
                    <div className="history-section-content">
                        {history.dental_records?.length > 0 ? (
                            history.dental_records.map((d, i) => (
                                <div key={i} className="history-card-premium">
                                    <div className="card-header">
                                        <span className="title">{getField(d, ['title', 'dental_procedure', 'Dental Procedure']) || '---'}</span>
                                        <span className="date">{formatDate(d.date || d.Date)}</span>
                                    </div>
                                    <div className="card-footer">
                                        <span className="vet"><i className="fas fa-user-md"></i> {getField(d, ['veterinarian', 'Veterinarian']) || '---'}</span>
                                    </div>
                                    {d.note && <p className="notes">{d.note}</p>}
                                </div>
                            ))
                        ) : <div className="empty-section">{t('receptionOutpatients.medicalHistory.sections.dental.empty')}</div>}
                    </div>
                );
            case 'deworming':
                return (
                    <div className="history-section-content">
                        {history.deworming_anti_flea_history?.length > 0 || history.deworming_anti_flea?.length > 0 ? (
                            [...(history.deworming_anti_flea_history || []), ...(history.deworming_anti_flea || [])].map((dew, i) => (
                                <div key={i} className="history-card-premium">
                                    <div className="card-header">
                                        <span className="title">{getField(dew, ['title', 'treatment_name', 'treatment', 'type', 'Type']) || '---'}</span>
                                        <span className="date">{formatDate(getField(dew, ['date', 'date_administered', 'Date']))}</span>
                                    </div>
                                    <div className="card-footer">
                                        <span className="vet"><i className="fas fa-user-md"></i> {getField(dew, ['veterinarian', 'Veterinarian']) || '---'}</span>
                                        {dew.next_due_date && <span className="next-due"><i className="fas fa-calendar-check"></i> Next: {formatDate(getField(dew, ['next_due_date', 'Next Due Date']))}</span>}
                                    </div>
                                    {dew.note && <p className="notes">{dew.note}</p>}
                                </div>
                            ))
                        ) : <div className="empty-section">{t('receptionOutpatients.medicalHistory.sections.deworming.empty')}</div>}
                    </div>
                );
            case 'past_visits':
                return (
                    <div className="history-section-content">
                        {loadingVisits ? (
                            <div className="tab-loading"><div className="spinner"></div><p>{t('common.loading')}</p></div>
                        ) : pastVisits.length > 0 ? (
                            <div className="attachments-grid" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {pastVisits.map((visit, i) => (
                                    <div key={i} className="history-card-premium" style={{ cursor: 'pointer' }} onClick={() => setSelectedVisit(visit)}>
                                        <div className="card-header">
                                            <span className="title" style={{ textTransform: 'capitalize' }}>
                                                {visit.type === 'inpatient'
                                                    ? t('receptionInpatients.title', { defaultValue: 'Inpatient Visit' })
                                                    : t('receptionOutpatients.title', { defaultValue: 'Outpatient Visit' })}
                                                {visit.serial_num && <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '8px' }}>#{visit.serial_num}</span>}
                                            </span>
                                            <span className="date">{formatDate(visit.visit_date || visit.created_at)}</span>
                                        </div>
                                        <div className="card-body">
                                            <div style={{ display: 'flex', gap: '15px' }}>
                                                <span><strong>Status:</strong> {visit.status}</span>
                                                {visit.reason && <span><strong>Reason:</strong> {visit.reason}</span>}
                                            </div>
                                        </div>
                                        <div className="card-footer">
                                            <span className="vet"><i className="fas fa-user-md"></i> {visit.doctor_name || '---'}</span>
                                            <span className="view-details" style={{ color: '#3b82f6', fontSize: '0.9em' }}>
                                                <i className="fas fa-eye"></i>{' '}
                                                {visit.can_view_full_soap === false
                                                    ? t('common.viewFinalDiagnosis', { defaultValue: 'View Final Diagnosis' })
                                                    : t('common.viewDetails', { defaultValue: 'View SOAP' })}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : <div className="empty-section">{t('common.noData')}</div>}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className={`emr-tab-content tab-content-inner ${isRTL ? 'rtl' : ''}`}>
            <div className="emr-horizontal-tabs">
                {internalTabs.map(tab => (
                    <button
                        key={tab.key}
                        className={`horizontal-tab-item ${activeInternalTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveInternalTab(tab.key)}
                    >
                        <i className={tab.icon}></i>
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>
            <div className="emr-content-area">
                <div className="section-header">
                    <h4>{internalTabs.find(t => t.key === activeInternalTab)?.label}</h4>
                    <button className="refresh-btn" onClick={fetchHistory} title="Refresh">
                        <i className="fas fa-sync-alt"></i>
                    </button>
                </div>
                {renderContent()}
            </div>

            {selectedVisit && selectedVisit.can_view_full_soap !== false && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }} onClick={() => setSelectedVisit(null)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        width: '90%',
                        maxWidth: '900px',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{
                            padding: '16px',
                            borderBottom: '1px solid #eee',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0 }}>{t('receptionInpatients.details.tabs.soapNotes', { defaultValue: 'SOAP Notes' })} - {formatDate(selectedVisit.visit_date || selectedVisit.created_at)}</h3>
                            <button onClick={() => setSelectedVisit(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            <SOAPDetailsContent
                                isVisible={true}
                                inpatientId={String(selectedVisit.type || '').toLowerCase() === 'inpatient' ? (selectedVisit.id || selectedVisit.visit_id || selectedVisit.inpatient_visit_id || selectedVisit.inpatient_id || selectedVisit.pet_visit_id) : null}
                                outpatientId={(String(selectedVisit.type || '').toLowerCase() === 'outpatient' || !selectedVisit.type) ? (selectedVisit.id || selectedVisit.visit_id || selectedVisit.outpatient_visit_id || selectedVisit.outpatient_id || selectedVisit.pet_visit_id) : null}
                                readOnly={true}
                                hideHeader={true}
                                petId={petId}
                                services={selectedVisit.services || selectedVisit.visit_details || selectedVisit.visitDetails || []}
                                visitInfo={{
                                    visitDate: selectedVisit.visit_date || selectedVisit.created_at,
                                    initialDiagnosis: selectedVisit.reason || '',
                                    finalDiagnosis: selectedVisit.final_diag || selectedVisit.final_diagnosis || '',
                                    doctorNotes: selectedVisit.doctor_notes || '',
                                    prescription: selectedVisit.prescription || ''
                                }}
                                onClose={() => setSelectedVisit(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {selectedVisit && selectedVisit.can_view_full_soap === false && (
                <RestrictedVisitModal visit={selectedVisit} onClose={() => setSelectedVisit(null)} />
            )}
        </div>
    );
};

export default MedicalHistoryTab;
